import classNames from "classnames";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./styles.scss";
import type { MaybeType } from "@/shared/types";
import { DeletedButton, EditButton } from "../Buttons";

export interface Props {
  onDelete: (...params: any[]) => void;
  onClickEdit?: () => void;
  onDeleteConfirm?: (...params: any[]) => void;
  setSwipedId?: (value: MaybeType<string>) => void;
  disabled?: boolean;
  height?: number;
  transitionDuration?: number;
  buttonsWidth?: number;
  deleteThreshold?: number;
  showDeleteAction?: boolean;
  className?: string;
  id?: string;
  rtl?: boolean;
  children?: React.ReactNode;
  hideEdit?: boolean;
  swipedId?: MaybeType<string>;
  currentId?: string;
}

const cursorPosition = (event: any) => {
  if (event?.touches?.[0]?.clientX) return event.touches[0].clientX;
  if (event?.clientX) return event?.clientX;
  if (event?.nativeEvent?.touches?.[0]?.clientX) return event.nativeEvent.touches[0].clientX;
  return event?.nativeEvent?.clientX;
};

export const SwipeToDeleteComponent = ({
  onDelete,
  onClickEdit,
  onDeleteConfirm,
  setSwipedId,
  disabled = false,
  height = 50,
  transitionDuration = 250,
  buttonsWidth = 100,
  deleteThreshold = 75,
  showDeleteAction = true,
  className = "",
  id = "",
  rtl = false,
  children,
  hideEdit,
  swipedId,
  currentId,
}: Props) => {
  if (swipedId?.length && swipedId !== currentId) disabled = true;

  const [touching, setTouching] = useState(false);
  const [translate, setTranslate] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const startTouchPosition = useRef(0);
  const initTranslate = useRef(0);
  const container = useRef<HTMLDivElement>(null);
  const containerWidth = useMemo(() => {
    return container.current?.getBoundingClientRect().width || 0;
  }, [container.current]);
  const deleteWithoutConfirmThreshold: number = containerWidth * (deleteThreshold / 100);

  const moveIsLimit = translate * -1 - 20 >= buttonsWidth;

  const onStart = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      if (disabled) return;
      if (touching) return;
      startTouchPosition.current = cursorPosition(event);
      initTranslate.current = translate;
      setTouching(true);
      setSwipedId && setSwipedId(currentId ?? "");
    },
    [disabled, touching, translate, currentId, setSwipedId],
  );

  useEffect(() => {
    const root = container.current;
    root?.style.setProperty("--rstdiHeight", "fit-content");
    root?.style.setProperty("--rstdiTransitionDuration", transitionDuration + "ms");
    root?.style.setProperty("--rstdiIsRtl", rtl ? "1" : "-1");
    root?.style.setProperty("--rstdibuttonsWidth", buttonsWidth + "px");
  }, [buttonsWidth, height, rtl, transitionDuration]);

  useEffect(() => {
    if (translate > buttonsWidth) return;
    const root = container.current;
    root?.style.setProperty("--rstdiTranslate", translate * (rtl ? -1 : 1) + "px");
    const shiftDelete = -translate >= deleteWithoutConfirmThreshold;
    root?.style.setProperty(
      `--rstdiButtonMargin${rtl ? "Right" : "Left"}`,
      (shiftDelete ? containerWidth + translate : containerWidth - buttonsWidth) + "px",
    );
  }, [translate, buttonsWidth, containerWidth, rtl, deleteWithoutConfirmThreshold]);

  const onMove = useCallback(
    function (event: TouchEvent | MouseEvent) {
      if (moveIsLimit) return;
      if (!touching) return;
      if (!rtl && cursorPosition(event) > startTouchPosition.current - initTranslate.current) {
        setSwipedId && setSwipedId("");
        return setTranslate(0);
      }
      if (rtl && cursorPosition(event) < startTouchPosition.current - initTranslate.current) {
        setSwipedId && setSwipedId("");
        return setTranslate(0);
      }
      setTranslate(cursorPosition(event) - startTouchPosition.current + initTranslate.current);
    },
    [rtl, touching, moveIsLimit, setSwipedId],
  );

  const onMouseMove = useCallback(
    function (event: MouseEvent): any {
      onMove(event);
    },
    [onMove],
  );

  const onTouchMove = useCallback(
    function (event: TouchEvent): any {
      event.preventDefault();
      onMove(event);
    },
    [onMove],
  );

  const onDeleteConfirmed = useCallback(() => {
    window.setTimeout(onDelete, transitionDuration);
  }, [onDelete, transitionDuration]);

  const onDeleteCancel = useCallback(() => {
    setTouching(() => false);
    setTranslate(() => 0);
    setDeleting(() => false);
    startTouchPosition.current = 0;
    initTranslate.current = 0;
  }, [onDelete, transitionDuration]);

  const onDeleteClick = useCallback(() => {
    setSwipedId && setSwipedId(currentId ?? "");
    if (onDeleteConfirm) {
      onDeleteConfirm(onDeleteConfirmed, onDeleteCancel);
    } else {
      onDeleteConfirmed();
    }
  }, [onDeleteConfirm, onDeleteConfirmed]);

  const onMouseUp = useCallback(
    function () {
      setTouching(() => false);
      startTouchPosition.current = 0;
      const acceptableMove = -buttonsWidth * 0.7;
      const showDelete = showDeleteAction ? (rtl ? -1 : 1) * translate < acceptableMove : false;
      const notShowDelete = showDeleteAction ? (rtl ? -1 : 1) * translate >= acceptableMove : true;
      const deleteWithoutConfirm = (rtl ? 1 : -1) * translate >= deleteWithoutConfirmThreshold;

      if (deleteWithoutConfirm) {
        setTranslate(() => -containerWidth);
      } else if (notShowDelete) {
        setSwipedId && setSwipedId("");
        setTranslate(() => 0);
      } else if (showDelete && !deleteWithoutConfirm) {
        setTranslate(() => (rtl ? 1 : -1) * buttonsWidth);
      }
    },
    [containerWidth, buttonsWidth, deleteWithoutConfirmThreshold, rtl, translate],
  );

  useEffect(() => {
    if (touching) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("touchend", onMouseUp);
    } else {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchend", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchend", onMouseUp);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, touching]);

  return (
    <div id={id} className={`rstdi${deleting ? " deleting" : ""} ${className}`} ref={container}>
      <div className={`delete${deleting ? " deleting" : ""}`}>
        <div className="rstdi-buttons" style={{ width: `${buttonsWidth}px`, height: "100%" }}>
          {!hideEdit && (
            <EditButton onClick={onClickEdit} className="rstdi-edit-button"></EditButton>
          )}
          <DeletedButton
            className={classNames("rstdi-delete-button", {
              ["hide-edit"]: hideEdit,
            })}
            onClick={onDeleteClick}
            buttonText=""
          />
        </div>
      </div>
      <div
        className={`content${deleting ? " deleting" : ""}${!touching ? " transition" : ""}`}
        onMouseDown={onStart}
        onTouchStart={onStart}
      >
        {children}
      </div>
    </div>
  );
};

const areEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.currentId === nextProps.currentId &&
    prevProps.swipedId === nextProps.swipedId &&
    prevProps.disabled === nextProps.disabled
  );
};

export const SwipeToDelete = memo(SwipeToDeleteComponent, areEqual);
